import {
  attachmentsCreateSignedUrlCreateMutation,
  DocumentCategoriesListData,
  documentCategoriesListInfiniteOptions,
  DocumentCategoryReadable,
  documentsBulkCreateCreateMutation,
  documentsCreateMutation,
  documentsListQueryKey,
  documentsPartialUpdateMutation,
  documentsRetrieveOptions,
  leadsDocumentsListQueryKey,
  LeadsListData,
  leadsListInfiniteOptions,
  opportunitiesDocumentsListQueryKey,
  PaginatedDocumentCategoryListReadable,
  PaginatedReturnLeadListReadable,
  ReturnLeadReadable
} from '@crm/api'
import { InfiniteSelect, Upload } from '@crm/components'
import {
  listTransformResults,
  onSubmitUploadDocument,
  userTransformResults
} from '@crm/utils'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  App,
  Col,
  Form,
  FormProps,
  Input,
  Radio,
  Row,
  Spin,
  UploadFile
} from 'antd'
import { UploadChangeParam } from 'antd/es/upload'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

type Props = {
  id?: string | number
  formName: string
  closeModal: () => void
}

const ALLOWED_TYPES = new Set<string>([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png'
])

export const DocumentForm = ({ id, formName, closeModal }: Props) => {
  const { t } = useLocales()
  const { id: leadId, opportunityId } = useParams()
  const [form] = Form.useForm()
  const [files, setFiles] = useState<UploadFile[]>([])
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const { message } = App.useApp()

  const { data, isLoading } = useQuery({
    ...documentsRetrieveOptions({
      path: { id: Number(id) }
    }),
    enabled: !!id,
    refetchOnMount: 'always'
  })

  useEffect(() => {
    if (data) {
      const { is_public, owner, lead, category, name, ...rest } = data

      form.setFieldsValue({
        ...rest,
        name,
        is_public: Number(is_public),
        owner: owner?.id,
        lead: lead?.id,
        category: category?.id
      })
    }
  }, [data, form])

  useEffect(() => {
    const fieldValue = form.getFieldsValue()
    const firstFileName = files[0]?.name

    if (files?.[0] && !fieldValue.name) {
      form.setFieldsValue({
        name: firstFileName
      })
    }
  }, [files, form])

  const onMutate = () => {
    invalidateQueries([
      documentsListQueryKey(),
      leadsDocumentsListQueryKey({
        path: { lead_id: leadId ?? '' }
      }),
      opportunitiesDocumentsListQueryKey({
        path: { opportunity_id: opportunityId ?? '' }
      })
    ])

    closeModal()
    form.resetFields()
    setFiles([])
    message.success(t('success.success_data_save'))
  }

  const { mutate: createMutate } = useMutation({
    ...documentsCreateMutation(),
    onSuccess: () => onMutate(),
    onError: (error) => showApiErrors(error, form)
  })

  const { mutate: bulkCreateMutate } = useMutation({
    ...documentsBulkCreateCreateMutation(),
    onSuccess: () => onMutate(),
    onError: (error) => showApiErrors(error, form)
  })

  const { mutate: editMutate } = useMutation({
    ...documentsPartialUpdateMutation(),
    onSuccess: () => onMutate(),
    onError: (error) => showApiErrors(error, form)
  })

  const { mutate: createAttachmentMutate } = useMutation({
    ...attachmentsCreateSignedUrlCreateMutation(),
    onSuccess: async (data) => {
      const signedUrl = data?.upload_url
      const originFileObj = files?.[0]?.originFileObj
      const fileType = files?.[0]?.type

      onSubmitUploadDocument(signedUrl, originFileObj, fileType)

      const { category, lead, ...rest } = form.getFieldsValue(true)

      const defaultData = {
        ...rest,
        category: category ?? null,
        lead: leadId ?? lead,
        opportunity: opportunityId
      }

      const body = {
        ...defaultData,
        file: undefined,
        attachment: data?.id
      }

      createMutate({ body })
    },

    onError: (error) => showApiErrors(error, form)
  })

  const onSubmit = () => {
    const { category, lead, attachmentId, ...rest } = form.getFieldsValue(true)

    const defaultData = {
      ...rest,
      category: category ?? null,
      lead: leadId ?? lead,
      opportunity: opportunityId
    }

    const body = {
      ...defaultData,
      attachment_id: attachmentId
    }

    if (id) {
      editMutate({
        path: { id: Number(id) },
        body: { ...body, attachment: body?.attachment?.id }
      })
    } else if (files.length <= 1) {
      createAttachmentMutate({ body: { title: rest?.name } })
    } else {
      bulkCreateMutate({ body })
    }
  }

  const onFieldsChange = (
    props: Parameters<Required<FormProps>['onFieldsChange']>[0]
  ) => {
    if (props[0]?.name?.includes('file')) setFiles(props[0]?.value ?? [])
  }

  const onSuccessUpload = (fileList: UploadFile[]) => {
    setFiles(
      fileList?.map((file) => ({
        ...file,
        uid: file.uid ?? String(Math.random()),
        file: file?.response?.url,
        name: file?.name
      })) as UploadFile[]
    )
  }

  return (
    <Spin spinning={isLoading}>
      <Form
        name={formName}
        form={form}
        layout="vertical"
        onFieldsChange={onFieldsChange}
        onFinish={onSubmit}
      >
        {!id && (
          <Row>
            <Col span={24}>
              <Form.Item
                name="file"
                label={t('files')}
                rules={[
                  {
                    required: true,
                    message: t('files_is_required')
                  }
                ]}
              >
                <Upload
                  pastable
                  subTitle={t('import.upload_support_single_or_bulk')}
                  fileList={files}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  beforeUpload={(file) => {
                    if (file.type && ALLOWED_TYPES.has(file.type)) return true
                    message.error(t('documents.invalid_file_type'))
                    return false
                  }}
                  onChange={({ fileList }: UploadChangeParam) => {
                    const validFiles = fileList?.filter(
                      (f) => f?.type && ALLOWED_TYPES.has(f?.type)
                    )
                    form.setFieldValue('file', validFiles)
                    setFiles(validFiles)
                    onSuccessUpload(validFiles)
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {files?.length <= 1 && (
          <>
            <Row>
              <Col span={24}>
                <Form.Item
                  name="name"
                  label={t('document.name')}
                  rules={[
                    { required: true, message: t('views.name_is_required') }
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="category" label={t('document.category')}>
                  <InfiniteSelect<
                    DocumentCategoriesListData,
                    PaginatedDocumentCategoryListReadable,
                    DocumentCategoryReadable
                  >
                    popupMatchSelectWidth
                    placeholder={t('main.select')}
                    getQueryFunction={documentCategoriesListInfiniteOptions}
                    getOptions={({ options }) => listTransformResults(options)}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="is_public" label={t('document.privacy')}>
                  <Radio.Group
                    defaultValue={0}
                    options={[
                      {
                        label: t('document.public'),
                        value: 1
                      },
                      {
                        label: t('document.private'),
                        value: 0
                      }
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row>
              {!leadId && (
                <Col span={24}>
                  <Form.Item name="lead" label={t('document.lead')}>
                    <InfiniteSelect<
                      LeadsListData,
                      PaginatedReturnLeadListReadable,
                      ReturnLeadReadable
                    >
                      popupMatchSelectWidth
                      getQueryFunction={leadsListInfiniteOptions}
                      placeholder={t('main.select')}
                      getOptions={({ options }) =>
                        userTransformResults(options)
                      }
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </>
        )}
      </Form>
    </Spin>
  )
}
